// ============================================================================
// Extensible Navigation Component
// Feature: 099-app-review-optimization + 035-app-shell-enhancement
// Created: 2026-01-11, Enhanced: 2026-01-21
// Description: Main navigation component with role-based menu filtering,
//              collapsible sidebar with localStorage persistence, tooltips
//              on hover when collapsed, and flyout menus for nested items.
// ============================================================================

import React, { useState, useMemo, useEffect } from 'react';
import { NavLink, useLocation, useSearchParams } from 'react-router-dom';
import clsx from 'clsx';
import {
  Activity, Archive, BarChart, Bell, Book, BookOpen, Bookmark, Building, Building2,
  CalendarClock, ChevronDown, ChevronRight, ChevronsLeft, ChevronsRight, Clock,
  ChevronsUpDown, Cloud, CloudUpload, Code, Database, ExternalLink,
  FileCode, FileText, Folder, FoldVertical, GitBranch, GitCompare,
  Globe, HardDrive, HeartPulse, HelpCircle, History, Home, Layers,
  LifeBuoy, List, ListChecks, Lock, Monitor, Network, PlayCircle,
  RefreshCw, ScrollText, Search, Server, Settings, Shield, ShieldCheck,
  Star, TrendingUp, UnfoldVertical, Users, Wrench, Zap, LayoutDashboard, ChartLine,
  Cable, Gauge, SlidersHorizontal, Rocket, Workflow, Settings2, PlugZap,
  Eye, EyeOff, DollarSign, AlertCircle,
  type LucideIcon,
} from 'lucide-react';
import { useCapacityFilterStore } from '../../stores/capacityFilterStore';
import { useAuthStore } from '../../stores/authStore';
import { toRole } from '@shared/access/roles';

import {
  menuSections,
  getMenuItemsBySection,
  MenuItemConfig,
  MenuSection,
  MenuSectionConfig,
} from './MenuConfig';
import { useAuth } from '../../hooks/useAuth';
// Feature 035: User preferences for sidebar state persistence
import { useUserPreferences } from '../../hooks/useUserPreferences';

interface ExtensibleNavigationProps {
  /** Override collapsed state (for external control) */
  collapsed?: boolean;
  /** Callback when collapsed state changes */
  onCollapsedChange?: (collapsed: boolean) => void;
  /** Additional CSS classes */
  className?: string;
  /** Spec 216 T010: clicking the user block opens the preferences panel */
  onUserClick?: () => void;
}

// Icon component — Lucide-backed, string-keyed so MenuConfig stays as-is.
// To use any icon: set icon: '<lucide-name-kebab-case>' in MenuConfig.ts,
// AND add the same key here AND to MobileNavigation.tsx's icon map — a name
// present in MenuConfig but missing from either map renders no icon there.
export const lucideIconMap: Record<string, LucideIcon> = {
  'activity': Activity,
  'archive': Archive,
  'bar-chart': BarChart,
  'bell': Bell,
  'book': Book,
  'book-open': BookOpen,
  'bookmark': Bookmark,
  'building': Building,
  'building-2': Building2,
  'cable': Cable,
  'calendar-clock': CalendarClock,
  // Infrastructure Trends. It was TrendingUp, the same glyph as 'trending-up' (Growth & Forecast): owner, 2026-11-02.
  'chart-line': ChartLine,
  'chevron-down': ChevronDown,
  'chevron-right': ChevronRight,
  'chevrons-up-down': ChevronsUpDown,
  'clock': Clock,
  'cloud': Cloud,
  'cloud-upload': CloudUpload,
  'code': Code,
  'database': Database,
  'dollar-sign': DollarSign,
  'external-link': ExternalLink,
  'file-code': FileCode,
  'file-text': FileText,
  'fold-vertical': FoldVertical,
  'folder': Folder,
  'gauge': Gauge,
  'git-branch': GitBranch,
  'git-compare': GitCompare,
  'globe': Globe,
  'hard-drive': HardDrive,
  'heart-pulse': HeartPulse,
  'alert-circle': AlertCircle,
  'help-circle': HelpCircle,
  'history': History,
  'home': Home,
  'layers': Layers,
  'layout-dashboard': LayoutDashboard,
  'life-buoy': LifeBuoy,
  'list': List,
  'list-checks': ListChecks,
  'lock': Lock,
  'menu-fold': ChevronsLeft,
  'menu-unfold': ChevronsRight,
  'monitor': Monitor,
  'network': Network,
  'play-circle': PlayCircle,
  'plug-zap': PlugZap,
  'refresh-cw': RefreshCw,
  'rocket': Rocket,
  'scroll-text': ScrollText,
  'search': Search,
  'server': Server,
  'settings': Settings,
  'settings-2': Settings2,
  'shield': Shield,
  'shield-check': ShieldCheck,
  'sliders-horizontal': SlidersHorizontal,
  'star': Star,
  'trending-up': TrendingUp,
  'unfold-vertical': UnfoldVertical,
  'users': Users,
  'workflow': Workflow,
  'wrench': Wrench,
  'zap': Zap,
};

const Icon: React.FC<{
  name: string;
  className?: string;
  'aria-label'?: string;
  'aria-hidden'?: boolean;
}> = ({ name, className, 'aria-label': ariaLabel, 'aria-hidden': ariaHidden = false }) => {
  const LucideComponent = lucideIconMap[name];
  if (!LucideComponent) {
    // An unknown icon name shows a visible "?" (iconCoverage.test.tsx keeps the real menu's icons
    // resolvable) but must keep the label it was given: a bare "?" became the collapsed link's
    // accessible name, so a screen reader announced "?" instead of the menu item.
    return (
      <span className={className} role={ariaLabel ? 'img' : undefined} aria-label={ariaLabel} aria-hidden={ariaHidden || !ariaLabel}>
        ?
      </span>
    );
  }
  return (
    <LucideComponent
      className={className}
      aria-label={ariaLabel}
      aria-hidden={ariaHidden || !ariaLabel}
      role={ariaLabel ? 'img' : undefined}
    />
  );
};

// Get initials from display name (first + last) or fall back to 'U'
const getInitials = (displayName?: string): string => {
  if (displayName?.trim()) {
    const parts = displayName.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
    }
    return parts[0].charAt(0).toUpperCase();
  }
  return 'U';
};

// Badge component for menu items
const MenuBadge: React.FC<{ badge: MenuItemConfig['badge'] }> = ({ badge }) => {
  if (!badge) return null;

  const label = badge.type === 'new' ? 'NEW' : badge.value;
  // A count/status badge with no wired value would render as an empty colored pill
  // (an unexplained little bar). Only show a badge when it actually has content.
  if (label === undefined || label === null || label === '' || label === 0) return null;

  const colorClasses = {
    red: 'bg-red-500 text-white',
    yellow: 'bg-yellow-500 text-black',
    green: 'bg-green-500 text-white',
    blue: 'bg-blue-500 text-white',
    gray: 'bg-gray-500 text-white',
  };

  return (
    <span
      className={clsx(
        'inline-flex items-center justify-center px-2 py-0.5 text-xs font-medium rounded-full',
        colorClasses[badge.color || 'gray']
      )}
    >
      {label}
    </span>
  );
};

// Single menu item component
const MenuItem: React.FC<{
  item: MenuItemConfig;
  collapsed: boolean;
  depth?: number;
  allItems?: MenuItemConfig[];
  expandAllTrigger?: number;
  collapseAllTrigger?: number;
}> = ({ item, collapsed, depth = 0, allItems = [], expandAllTrigger = 0, collapseAllTrigger = 0 }) => {
  const location = useLocation();
  const hasChildren = item.children && item.children.length > 0;
  
  // Check if another menu item has a more specific match for the current path
  // This prevents parent routes from highlighting when a child route is active
  const hasMoreSpecificMatch = allItems.some(otherItem => 
    otherItem.id !== item.id && 
    otherItem.path !== item.path &&
    otherItem.path !== '#' &&
    item.path !== '#' &&
    otherItem.path.startsWith(item.path + '/') &&
    (location.pathname === otherItem.path || location.pathname.startsWith(otherItem.path + '/'))
  );
  
  // Non-navigable items (path === '#') are never active
  // Only use startsWith matching if no other item has a more specific match
  const isActive = item.path === '#' 
    ? false
    : hasMoreSpecificMatch 
      ? location.pathname === item.path
      : location.pathname === item.path || location.pathname.startsWith(item.path + '/');
  
  // Auto-expand if any child is active
  const hasActiveChild = hasChildren && item.children!.some(child => 
    location.pathname === child.path || location.pathname.startsWith(child.path + '/')
  );
  
  const [isExpanded, setIsExpanded] = useState(hasActiveChild);
  const [showFlyout, setShowFlyout] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  // React to expand all trigger
  useEffect(() => {
    if (expandAllTrigger > 0 && hasChildren) {
      setIsExpanded(true);
    }
  }, [expandAllTrigger, hasChildren]);

  // React to collapse all trigger
  useEffect(() => {
    if (collapseAllTrigger > 0 && hasChildren) {
      setIsExpanded(false);
    }
  }, [collapseAllTrigger, hasChildren]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'Enter':
      case ' ':
        // Let Enter/Space navigate via NavLink - don't prevent default
        break;
      case 'ArrowRight':
        if (hasChildren && !collapsed) {
          e.preventDefault();
          setIsExpanded(true);
        }
        break;
      case 'ArrowLeft':
        if (hasChildren && !collapsed && isExpanded) {
          e.preventDefault();
          setIsExpanded(false);
        }
        break;
      case 'Escape':
        if (collapsed && showFlyout) {
          e.preventDefault();
          setShowFlyout(false);
        }
        break;
    }
  };

  const handleMouseEnter = () => {
    if (collapsed && depth === 0) {
      setShowTooltip(true);
      if (hasChildren) {
        setShowFlyout(true);
      }
    }
  };

  const handleMouseLeave = () => {
    if (collapsed) {
      setShowTooltip(false);
      setShowFlyout(false);
    }
  };

  const content = (
    <>
      {item.icon && (
        <Icon
          name={item.icon}
          className={clsx('w-5 h-5 flex-shrink-0', collapsed && depth === 0 && 'mx-auto')}
          aria-hidden={!collapsed}
          aria-label={collapsed ? `${item.label} icon` : undefined}
        />
      )}
      {!collapsed && (
        <span className="flex-1 ml-3 text-left">{item.label}</span>
      )}
      {!collapsed && item.badge && <MenuBadge badge={item.badge} />}
      {!collapsed && hasChildren && (
        <Icon
          name={isExpanded ? 'chevron-down' : 'chevron-right'}
          className="w-4 h-4 flex-shrink-0"
          aria-hidden={true}
        />
      )}
      {!collapsed && item.isExternal && (
        <Icon 
          name="external-link" 
          className="w-4 h-4 ml-1"
          aria-label="External link icon"
        />
      )}
    </>
  );

  const linkClasses = clsx(
    'flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors relative',
    depth > 0 && 'ml-6',
    collapsed && depth === 0 && 'justify-center',
    isActive
      ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-200'
      : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-subtle)]'
  );

  // Handle expand/collapse toggle for items with children
  const handleToggleExpand = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (collapsed) {
      setShowFlyout(!showFlyout);
    } else {
      setIsExpanded(!isExpanded);
    }
  };

  // Check if this is a non-navigable parent (path === '#')
  const isNonNavigable = item.path === '#';

  return (
    <div 
      className="relative group"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {hasChildren ? (
        // Parent with children
        <div className="flex items-center">
          {isNonNavigable ? (
            // Non-navigable parent: use button that just toggles expand
            <button
              onClick={handleToggleExpand}
              className={clsx(linkClasses, 'flex-1 cursor-pointer')}
              data-testid="nav-item-with-submenu"
              onKeyDown={handleKeyDown}
              title={item.description || item.label}
            >
              {item.icon && (
                <Icon
                  name={item.icon}
                  className={clsx('w-5 h-5 flex-shrink-0', collapsed && depth === 0 && 'mx-auto')}
                  aria-hidden={!collapsed}
                  aria-label={collapsed ? `${item.label} icon` : undefined}
                />
              )}
              {!collapsed && (
                <span className="flex-1 ml-3 text-left">{item.label}</span>
              )}
              {!collapsed && item.badge && <MenuBadge badge={item.badge} />}
              {!collapsed && (
                <Icon
                  name={isExpanded ? 'chevron-down' : 'chevron-right'}
                  className="w-4 h-4 flex-shrink-0"
                  aria-hidden={true}
                />
              )}
            </button>
          ) : (
            // Navigable parent: use NavLink for the main click, separate button for expand
            <>
              <NavLink
                to={item.path}
                className={clsx(linkClasses, 'flex-1')}
                data-testid="nav-item-with-submenu"
                target={item.isExternal ? '_blank' : undefined}
                rel={item.isExternal ? 'noopener noreferrer' : undefined}
                onKeyDown={handleKeyDown}
                title={item.description || item.label}
              >
                {item.icon && (
                  <Icon
                    name={item.icon}
                    className={clsx('w-5 h-5 flex-shrink-0', collapsed && depth === 0 && 'mx-auto')}
                    aria-hidden={!collapsed}
                    aria-label={collapsed ? `${item.label} icon` : undefined}
                  />
                )}
                {!collapsed && (
                  <span className="flex-1 ml-3 text-left">{item.label}</span>
                )}
                {!collapsed && item.badge && <MenuBadge badge={item.badge} />}
              </NavLink>
              {!collapsed && (
                <button
                  onClick={handleToggleExpand}
                  className="p-1 rounded hover:bg-[var(--color-bg-subtle)] transition-colors"
                  aria-label={isExpanded ? 'Collapse submenu' : 'Expand submenu'}
                  aria-expanded={isExpanded}
                >
                  <Icon
                    name={isExpanded ? 'chevron-down' : 'chevron-right'}
                    className="w-4 h-4 flex-shrink-0"
                    aria-hidden={true}
                  />
                </button>
              )}
            </>
          )}
        </div>
      ) : (
        <NavLink
          to={item.path}
          className={linkClasses}
          data-testid="nav-item"
          target={item.isExternal ? '_blank' : undefined}
          rel={item.isExternal ? 'noopener noreferrer' : undefined}
          onKeyDown={handleKeyDown}
          title={item.description || item.label}
        >
          {content}
        </NavLink>
      )}


      {/* Tooltip for collapsed items */}
      {collapsed && showTooltip && depth === 0 && !hasChildren && (
        <div 
          data-testid="nav-tooltip"
          className="absolute left-full top-1/2 -translate-y-1/2 ml-2 z-[100] pointer-events-none"
        >
          <div className="bg-[var(--color-bg-card)] text-[var(--color-text-primary)] border border-[var(--color-border)] text-sm px-3 py-2 rounded shadow-lg whitespace-nowrap">
            <div className="font-medium">{item.label}</div>
            {item.description && (
              <div className="text-xs text-[var(--color-text-secondary)] mt-0.5">{item.description}</div>
            )}
            <div className="absolute top-1/2 left-0 transform -translate-x-full -translate-y-1/2">
              <div className="border-4 border-transparent border-r-[var(--color-bg-card)]"></div>
            </div>
          </div>
        </div>
      )}

      {/* Flyout menu for collapsed items with children */}
      {collapsed && showFlyout && hasChildren && depth === 0 && (
        <div 
          data-testid="nav-flyout"
          className="absolute left-full top-0 pl-2 z-50"
        >
          <div className="bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded-lg shadow-lg py-2 min-w-48">
            <div className="px-3 py-2 text-xs font-semibold text-[var(--color-text-secondary)] uppercase tracking-wider border-b border-[var(--color-border)]">
              {item.label}
            </div>
            <div className="py-1">
              {item.children!.map((child) => (
                <NavLink
                  key={child.id}
                  to={child.path}
                  data-testid="nav-flyout-item"
                  className={({ isActive }) => clsx(
                    'block px-3 py-2 text-sm transition-colors',
                    isActive
                      ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-200'
                      : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-subtle)]'
                  )}
                  target={child.isExternal ? '_blank' : undefined}
                  rel={child.isExternal ? 'noopener noreferrer' : undefined}
                >
                  <div className="flex items-center">
                    {child.icon && <Icon name={child.icon} className="w-4 h-4 mr-3" />}
                    <span>{child.label}</span>
                    {child.badge && <MenuBadge badge={child.badge} />}
                    {child.isExternal && <Icon name="external-link" className="w-4 h-4 ml-auto" />}
                  </div>
                </NavLink>
              ))}
            </div>
            <div className="absolute top-2 left-0 transform -translate-x-full">
              <div className="border-4 border-transparent border-r-[var(--color-bg-card)]"></div>
            </div>
          </div>
        </div>
      )}
      
      {hasChildren && isExpanded && !collapsed && (
        <div className="mt-1 space-y-1">
          {item.children!.map((child) => (
            <MenuItem 
              key={child.id} 
              item={child} 
              collapsed={collapsed} 
              depth={depth + 1} 
              allItems={allItems} 
              expandAllTrigger={expandAllTrigger}
              collapseAllTrigger={collapseAllTrigger}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Favorites Section Component
const FavoritesSection: React.FC<{ collapsed: boolean }> = ({ collapsed }) => {
  const { preferences } = useUserPreferences();
  const [showTooltip, setShowTooltip] = useState(false);
  
  if (preferences.favorites.length === 0) {
    return null;
  }

  return (
    <div data-testid="favorites-section" className="mb-4">
      {!collapsed && (
        <div className="px-2 mb-2">
          <h3 className="text-xs font-semibold text-[var(--color-text-secondary)] uppercase tracking-wider">
            Favorites
          </h3>
        </div>
      )}
      
      <div data-testid="favorites-list" className="space-y-1">
        {preferences.favorites.map((favorite) => (
          <div key={favorite.id} className="relative">
            <NavLink
              to={favorite.path}
              data-testid="favorite-item"
              className={({ isActive }) =>
                clsx(
                  'flex items-center px-2 py-2 text-sm font-medium rounded-lg transition-colors',
                  isActive
                    ? 'bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-200'
                    : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-subtle)]',
                  collapsed && 'justify-center px-2'
                )
              }
              onMouseEnter={() => collapsed && setShowTooltip(true)}
              onMouseLeave={() => collapsed && setShowTooltip(false)}
              title={collapsed ? favorite.title : undefined}
            >
              <Icon name="star" className="w-5 h-5 flex-shrink-0 text-yellow-500" />
              {!collapsed && (
                <span className="ml-3 truncate">{favorite.title}</span>
              )}
            </NavLink>
            
            {/* Tooltip for collapsed state */}
            {collapsed && showTooltip && (
              <div className="absolute left-full top-1/2 transform -translate-y-1/2 ml-2 z-50">
                <div className="bg-[var(--color-bg-card)] text-[var(--color-text-primary)] border border-[var(--color-border)] text-xs rounded px-2 py-1 whitespace-nowrap">
                  {favorite.title}
                  <div className="absolute top-1/2 left-0 transform -translate-y-1/2 -translate-x-full">
                    <div className="border-4 border-transparent border-r-[var(--color-bg-card)]"></div>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// Section component
// T043: Section collapse/expand state persists via localStorage
const SECTION_STATE_KEY = 'fcp-nav-sections';

const getSectionStates = (): Record<string, boolean> => {
  try {
    const stored = localStorage.getItem(SECTION_STATE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
};

const saveSectionState = (sectionId: string, expanded: boolean) => {
  try {
    const states = getSectionStates();
    states[sectionId] = expanded;
    localStorage.setItem(SECTION_STATE_KEY, JSON.stringify(states));
  } catch {
    // Ignore storage errors
  }
};

const MenuSectionComponent: React.FC<{
  section: MenuSectionConfig;
  items: MenuItemConfig[];
  collapsed: boolean;
  allItems: MenuItemConfig[];
  expandAllTrigger?: number;
  collapseAllTrigger?: number;
}> = ({ section, items, collapsed, allItems, expandAllTrigger = 0, collapseAllTrigger = 0 }) => {
  // T043: Load initial state from localStorage, fallback to defaultCollapsed
  const [isExpanded, setIsExpanded] = useState(() => {
    const states = getSectionStates();
    return states[section.id] !== undefined 
      ? states[section.id] 
      : !section.defaultCollapsed;
  });

  // React to expand all trigger
  useEffect(() => {
    if (expandAllTrigger > 0 && section.collapsible) {
      setIsExpanded(true);
      saveSectionState(section.id, true);
    }
  }, [expandAllTrigger, section.collapsible, section.id]);

  // React to collapse all trigger
  useEffect(() => {
    if (collapseAllTrigger > 0 && section.collapsible) {
      setIsExpanded(false);
      saveSectionState(section.id, false);
    }
  }, [collapseAllTrigger, section.collapsible, section.id]);

  // T043: Persist section state when it changes
  const handleToggleSection = () => {
    const newExpanded = !isExpanded;
    setIsExpanded(newExpanded);
    saveSectionState(section.id, newExpanded);
  };

  if (section.id === 'dashboards' || !section.collapsible) {
    return (
      <div className="space-y-1">
        {!collapsed && section.label && (
          <div className="px-3 py-2 text-xs font-semibold text-[var(--color-text-secondary)] uppercase tracking-wider">
            {section.label}
          </div>
        )}
        {items.map((item) => (
          <MenuItem 
            key={item.id} 
            item={item} 
            collapsed={collapsed} 
            allItems={allItems} 
            expandAllTrigger={expandAllTrigger}
            collapseAllTrigger={collapseAllTrigger}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="pt-4">
      {!collapsed && (
        <button
          onClick={handleToggleSection}
          className="flex items-center w-full px-3 py-2 text-xs font-semibold text-[var(--color-text-secondary)] uppercase tracking-wider hover:text-[var(--color-text-primary)]"
        >
          <span className="flex-1 text-left">{section.label}</span>
          <Icon name={isExpanded ? 'chevron-down' : 'chevron-right'} className="w-4 h-4" />
        </button>
      )}
      {(isExpanded || collapsed) && (
        <div className="mt-1 space-y-1">
          {items.map((item) => (
            <MenuItem 
              key={item.id} 
              item={item} 
              collapsed={collapsed} 
              allItems={allItems} 
              expandAllTrigger={expandAllTrigger}
              collapseAllTrigger={collapseAllTrigger}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export const ExtensibleNavigation: React.FC<ExtensibleNavigationProps> = ({
  collapsed: externalCollapsed,
  onCollapsedChange,
  className,
  onUserClick,
}) => {
  const { user } = useAuth();
  const { sidebarCollapsed, toggleSidebar, setSidebarCollapsed } = useUserPreferences();
  const [searchParams] = useSearchParams();
  
  // Use external collapsed prop if provided, otherwise use preferences
  const collapsed = externalCollapsed ?? sidebarCollapsed;
  
  // Map auth role to nav role format
  const mapAuthRole = (role: string | undefined): string => {
    const roleMap: Record<string, string> = {
      'admin': 'Admin',
      'operator': 'Operator',
      'manager': 'Manager',
      'user': 'User',
      'viewer': 'Viewer',
      // Spec 285 — SuperAdmin is now a real enforced role.
      'superadmin': 'SuperAdmin',
    };
    return roleMap[role?.toLowerCase() || ''] || 'User';
  };

  // Check for role override from query params (dev mode)
  const roleOverride = searchParams.get('role');
  const authRole = mapAuthRole(user?.role);
  const userRole = roleOverride && ['Admin', 'Operator', 'Manager', 'User', 'Viewer', 'SuperAdmin'].includes(roleOverride)
    ? roleOverride
    : authRole;
    
  // Spec 285 — the viewing tenant's licenses gate Backup Management / Pipeline Monitoring.
  const licenses = useAuthStore((s) => s.licenses);
  // Principle LIII: parse the role with the shared strict parser instead of `as any`. A retired
  // or unknown role (e.g. 'User') is not a role the menu knows; it shows nothing, as it did
  // before the cast hid the mismatch.
  const navRole = toRole(userRole);
  const menuBySection = useMemo(
    () => (navRole ? getMenuItemsBySection(navRole, licenses) : new Map<MenuSection, MenuItemConfig[]>()),
    [navRole, licenses],
  );
  
  // Collect all menu items (including children) for active state detection
  const allMenuItems = useMemo(() => {
    const items: MenuItemConfig[] = [];
    const collectItems = (menuItems: MenuItemConfig[]) => {
      for (const item of menuItems) {
        items.push(item);
        if (item.children && item.children.length > 0) {
          collectItems(item.children);
        }
      }
    };
    menuBySection.forEach((sectionItems) => {
      collectItems(sectionItems);
    });
    return items;
  }, [menuBySection]);

  const handleToggleCollapsed = () => {
    const newCollapsed = !collapsed;
    
    // Update preferences if not externally controlled
    if (externalCollapsed === undefined) {
      setSidebarCollapsed(newCollapsed);
    }
    
    // Call external callback if provided
    onCollapsedChange?.(newCollapsed);
  };

  // Trigger counters for expand/collapse all
  const [expandAllTrigger, setExpandAllTrigger] = useState(0);
  const [collapseAllTrigger, setCollapseAllTrigger] = useState(0);
  const { showPausedUnknown, toggle: togglePausedUnknown } = useCapacityFilterStore();

  const handleExpandAll = () => {
    setExpandAllTrigger(prev => prev + 1);
  };

  const handleCollapseAll = () => {
    setCollapseAllTrigger(prev => prev + 1);
  };

  return (
    <nav
      data-testid="extensible-navigation"
      className={clsx(
        'flex flex-col h-full bg-[var(--color-bg-card)] border-r border-[var(--color-border)] transition-all duration-300',
        collapsed ? 'w-16 collapsed overflow-visible' : 'w-56',
        className
      )}
    >
      {/* Controls row — Collapse/Expand All + the collapse-toggle chevron on one
          line (brand title removed per Spec 197 feedback). */}
      <div className={clsx(
        'flex items-center gap-1 border-b border-[var(--color-border)] bg-[var(--color-bg-subtle)] px-2 py-2',
        collapsed ? 'justify-center' : 'justify-between'
      )}>
        {!collapsed && (
          <div className="flex items-center gap-0.5">
            <button
              data-testid="collapse-all-button"
              onClick={handleCollapseAll}
              className="flex items-center gap-1 rounded-md px-1.5 py-1 text-[11px] font-medium text-[var(--color-text-secondary)] transition-colors hover:bg-[var(--color-bg-card)] hover:text-[var(--color-text-primary)]"
              aria-label="Collapse all submenus"
              title="Collapse all submenus"
            >
              <Icon name="fold-vertical" className="h-3.5 w-3.5" aria-hidden={true} />
              <span>Collapse All</span>
            </button>
            <div className="h-4 w-px bg-[var(--color-border)]" />
            <button
              data-testid="expand-all-button"
              onClick={handleExpandAll}
              className="flex items-center gap-1 rounded-md px-1.5 py-1 text-[11px] font-medium text-[var(--color-text-secondary)] transition-colors hover:bg-[var(--color-bg-card)] hover:text-[var(--color-text-primary)]"
              aria-label="Expand all submenus"
              title="Expand all submenus"
            >
              <Icon name="unfold-vertical" className="h-3.5 w-3.5" aria-hidden={true} />
              <span>Expand All</span>
            </button>
          </div>
        )}
        <button
          data-testid="collapse-button"
          onClick={handleToggleCollapsed}
          className="rounded-md p-1.5 text-[var(--color-text-secondary)] transition-colors hover:bg-[var(--color-bg-card)] hover:text-[var(--color-text-primary)]"
          aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
          title={collapsed ? 'Expand navigation' : 'Collapse navigation'}
        >
          <Icon
            name={collapsed ? 'menu-unfold' : 'menu-fold'}
            className="h-5 w-5"
            aria-label={collapsed ? 'Expand navigation icon' : 'Collapse navigation icon'}
          />
        </button>
      </div>

      {/* Menu Items */}
      <div 
        data-testid="nav-scroll-container"
        className={clsx(
          // Always allow vertical scroll (collapsed sidebars with many items were
          // stuck on overflow-visible = unreachable lower items). overflow-x-hidden
          // keeps the narrow rail tidy; collapsed hover tooltips use native titles.
          'flex-1 overflow-y-auto overflow-x-hidden px-2 py-4'
        )}
        style={{ maxHeight: 'calc(100vh - 140px)' }}
      >
        <div data-testid="nav-list">
          {/* Favorites Section */}
          <FavoritesSection collapsed={collapsed} />
          
          {menuSections.map((section) => {
            const items = menuBySection.get(section.id);
            if (!items || items.length === 0) return null;

            return (
              <MenuSectionComponent
                key={section.id}
                section={section}
                items={items}
                collapsed={collapsed}
                allItems={allMenuItems}
                expandAllTrigger={expandAllTrigger}
                collapseAllTrigger={collapseAllTrigger}
              />
            );
          })}
        </div>
      </div>

      {/* User Info — Spec 216 T010: opens the user's preferences panel */}
      <div className="border-t border-[var(--color-border)] px-4 py-3">
        <button
          type="button"
          data-testid="nav-user-block"
          onClick={onUserClick}
          disabled={!onUserClick}
          title="Open your preferences"
          className={clsx(
            'flex w-full items-center rounded-lg text-left transition-colors',
            collapsed && 'justify-center',
            onUserClick && 'cursor-pointer hover:bg-[var(--color-bg-subtle)]'
          )}
        >
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[var(--color-accent)] font-medium text-white">
            {getInitials(user?.displayName)}
          </div>
          {!collapsed && (
            <div className="ml-3">
              <p className="text-sm font-medium text-[var(--color-text-primary)]">
                {user?.displayName || 'User'}
              </p>
              <p className="text-xs text-[var(--color-text-secondary)]">{userRole}</p>
            </div>
          )}
        </button>
      </div>
    </nav>
  );
};

export default ExtensibleNavigation;
